/* eslint-disable max-lines */
import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  Bold,
  Check,
  ChevronDown,
  Columns2,
  Copy,
  Download,
  FilePlus2,
  FileText,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Maximize2,
  MessageCirclePlus,
  Minimize2,
  MoreHorizontal,
  Redo,
  ShieldCheck,
  Sparkles,
  Type,
  Underline,
  Undo,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type DiffViewMode = "side-by-side" | "inline-highlights"

export function DemoCanvas() {
  const { t } = useTranslation("", { keyPrefix: "training.canvas_demo" })
  const [isCanvasHighlighted, setIsCanvasHighlighted] = useState(false)
  const [showProposalBubble, setShowProposalBubble] = useState(false)
  const [showDiffPreview, setShowDiffPreview] = useState(false)
  const [showChatHint, setShowChatHint] = useState(false)
  const [hasPendingSuggestion, setHasPendingSuggestion] = useState(false)
  const [isSuggestionAccepted, setIsSuggestionAccepted] = useState(false)
  const [diffViewMode, setDiffViewMode] = useState<DiffViewMode>("side-by-side")
  const [showFullDiff, setShowFullDiff] = useState(false)
  const highlights = [
    t("highlights.0", "Onboarding drop-off around day 3."),
    t("highlights.1", "Pricing objections tied to seat minimums."),
    t("highlights.2", "Missing Slack and HubSpot integrations."),
  ]
  const nextSteps = [
    t("next_steps.0", "Add a guided 10-minute setup flow."),
    t("next_steps.1", "Launch a starter tier without seat minimums."),
    t("next_steps.2", "Ship Slack and HubSpot connectors."),
  ]
  const showAddedSection = hasPendingSuggestion || isSuggestionAccepted

  const contextOriginalLines = useMemo(
    () => [
      t("diff.context.original.0", "### Key drivers"),
      t("diff.context.original.1", "- Onboarding drop-off around day 3."),
      t("diff.context.original.2", "- Pricing objections tied to seat minimums."),
      "",
      t("diff.context.original.4", "### Next steps"),
      t("diff.context.original.5", "- Add a guided 10-minute setup flow."),
      t("diff.context.original.6", "- Launch a starter tier without seat minimums."),
    ],
    [t]
  )
  const contextModifiedLines = useMemo(
    () => [
      t("diff.context.modified.0", "### Key drivers"),
      t("diff.context.modified.1", "- Onboarding drop-off around day 3."),
      t("diff.context.modified.2", "- Pricing objections tied to seat minimums."),
      "",
      t("diff.context.modified.4", "### Economy section"),
      t(
        "diff.context.modified.5",
        "Economic uncertainty is extending sales cycles across EMEA and NA."
      ),
      t(
        "diff.context.modified.6",
        "Recommend phased rollout and cost-sensitive packaging options."
      ),
      "",
      t("diff.context.modified.8", "### Next steps"),
      t("diff.context.modified.9", "- Add a guided 10-minute setup flow."),
      t("diff.context.modified.10", "- Launch a starter tier without seat minimums."),
    ],
    [t]
  )
  const fullOriginalLines = useMemo(
    () => [
      t("diff.full.original.0", "## Q4 Retention Summary"),
      t(
        "diff.full.original.1",
        "Turn a strong answer into a shareable brief by tightening structure and tone."
      ),
      "",
      t("diff.full.original.3", "### Key drivers"),
      t("diff.full.original.4", "- Onboarding drop-off around day 3."),
      t("diff.full.original.5", "- Pricing objections tied to seat minimums."),
      t("diff.full.original.6", "- Missing Slack and HubSpot integrations."),
      "",
      t("diff.full.original.8", "### Next steps"),
      t("diff.full.original.9", "- Add a guided 10-minute setup flow."),
      t("diff.full.original.10", "- Launch a starter tier without seat minimums."),
      t("diff.full.original.11", "- Ship Slack and HubSpot connectors."),
    ],
    [t]
  )
  const fullModifiedLines = useMemo(
    () => [
      t("diff.full.modified.0", "## Q4 Retention Summary"),
      t(
        "diff.full.modified.1",
        "Turn a strong answer into a shareable brief by tightening structure and tone."
      ),
      "",
      t("diff.full.modified.3", "### Key drivers"),
      t("diff.full.modified.4", "- Onboarding drop-off around day 3."),
      t("diff.full.modified.5", "- Pricing objections tied to seat minimums."),
      t("diff.full.modified.6", "- Missing Slack and HubSpot integrations."),
      "",
      t("diff.full.modified.8", "### Economy section"),
      t(
        "diff.full.modified.9",
        "Economic uncertainty is extending sales cycles across EMEA and NA."
      ),
      t("diff.full.modified.10", "Recommend phased rollout and cost-sensitive packaging options."),
      "",
      t("diff.full.modified.12", "### Next steps"),
      t("diff.full.modified.13", "- Add a guided 10-minute setup flow."),
      t("diff.full.modified.14", "- Launch a starter tier without seat minimums."),
      t("diff.full.modified.15", "- Ship Slack and HubSpot connectors."),
    ],
    [t]
  )
  const displayOriginalLines = showFullDiff ? fullOriginalLines : contextOriginalLines
  const displayModifiedLines = showFullDiff ? fullModifiedLines : contextModifiedLines
  const changedOriginalLines = useMemo(() => new Set<number>(), [])
  const changedModifiedLines = useMemo(
    () => (showFullDiff ? new Set<number>([8, 9, 10, 11]) : new Set<number>([4, 5, 6, 7])),
    [showFullDiff]
  )
  const originalDiffText = displayOriginalLines.join("\n")
  const modifiedDiffText = displayModifiedLines.join("\n")

  const handleCanvasHighlight = () => {
    setShowProposalBubble(false)
    setShowDiffPreview(false)
    setShowChatHint(false)
    setIsCanvasHighlighted(true)
    window.setTimeout(() => setIsCanvasHighlighted(false), 2000)
  }

  const handleGenerateProposal = () => {
    setShowProposalBubble(true)
    setShowDiffPreview(false)
    setShowChatHint(false)
  }

  const handleApplyProposal = () => {
    setHasPendingSuggestion(true)
    setIsSuggestionAccepted(false)
    setShowProposalBubble(false)
    setShowDiffPreview(false)
  }

  const handleDiscardProposal = () => {
    setShowProposalBubble(false)
    setShowDiffPreview(false)
  }

  const handleAcceptPendingSuggestion = () => {
    setHasPendingSuggestion(false)
    setIsSuggestionAccepted(true)
    setShowDiffPreview(false)
  }

  const handleDiscardPendingSuggestion = () => {
    setHasPendingSuggestion(false)
    setIsSuggestionAccepted(false)
    setShowDiffPreview(false)
  }

  const handleAcceptFromDiff = () => {
    if (hasPendingSuggestion) {
      handleAcceptPendingSuggestion()
      return
    }
    handleApplyProposal()
  }

  const handleRejectFromDiff = () => {
    if (hasPendingSuggestion) {
      handleDiscardPendingSuggestion()
      return
    }
    handleDiscardProposal()
  }

  const handleStartChatWithDocument = () => {
    setShowChatHint(true)
    setShowProposalBubble(false)
    setShowDiffPreview(false)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-background w-fit self-start rounded-lg border px-2 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button className="size-7" size="icon" type="button" variant="icon">
            <Copy className="size-4" />
          </Button>
          <Button className="size-7" size="icon" type="button" variant="icon">
            <Download className="size-4" />
          </Button>
          <Tooltip open>
            <TooltipTrigger asChild>
              <Button
                className="border-foreground bg-accent text-accent-foreground size-7 border"
                size="icon"
                type="button"
                variant="icon"
                onClick={handleCanvasHighlight}
              >
                <FilePlus2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>
              <p>{t("create_document", "Create Document")}</p>
            </TooltipContent>
          </Tooltip>
          <Button className="h-7 gap-1 px-2 text-xs" size="sm" type="button" variant="outline">
            <ShieldCheck className="size-3.5" />
            {t("fact_check", "Fact check")}
            <ChevronDown className="size-3.5" />
          </Button>
          <Button className="size-7" size="icon" type="button" variant="icon">
            <MoreHorizontal className="size-4" />
          </Button>
        </div>
      </div>
      <div
        className={`mt-6 rounded-xl transition-shadow duration-300 ${
          isCanvasHighlighted
            ? "ring-primary/40 shadow-[0_0_0.75rem_hsl(var(--primary)/0.2)] ring-2"
            : ""
        }`}
      >
        <div className="bg-muted overflow-hidden rounded-xl border">
          <div className="border-border/40 bg-background flex flex-wrap items-center justify-between gap-2 border-b px-3 py-3 sm:px-4">
            <div className="min-w-0">
              <p className="text-foreground text-sm font-semibold">{t("title", "Canvas editor")}</p>
              <p className="text-muted-foreground text-xs">
                {t("subtitle", "Updated 1 minute ago")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button size="icon" type="button" variant="ghost">
                <MoreHorizontal className="size-4" />
              </Button>
              <Button size="icon" type="button" variant="ghost">
                <Download className="size-4" />
              </Button>
              <Button
                className="gap-1.5 px-2 text-xs"
                size="sm"
                type="button"
                variant="outline"
                onClick={handleStartChatWithDocument}
              >
                <MessageCirclePlus className="size-3.5" />
                {t("chat_with_document", "Chat with document")}
              </Button>
              <div className="text-muted-foreground w-[8.5rem] px-2 text-center text-xs">
                <span>{t("autosave_saved", "All changes saved")}</span>
              </div>
              <Button size="icon" type="button" variant="ghost">
                <X className="size-4" />
              </Button>
            </div>
          </div>
          <div className="border-border/40 bg-background border-b px-3 py-2 sm:px-4">
            <div className="bg-background mx-auto flex w-fit max-w-full flex-nowrap items-center gap-0.5 overflow-x-auto rounded-xl border px-3 py-1.5 shadow-[0_2px_12px_hsl(var(--foreground)/0.08)]">
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Undo className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Redo className="size-4" />
              </Button>

              <div className="bg-border mx-0.5 h-5 w-px" />

              <Button className="h-7 gap-0.5 px-2 text-xs" size="sm" type="button" variant="ghost">
                {t("toolbar.paragraph", "Paragraph")}
                <ChevronDown className="size-3" />
              </Button>
              <Button
                className="h-7 w-[86px] justify-between gap-0.5 px-2 text-xs"
                size="sm"
                type="button"
                variant="ghost"
              >
                <span className="truncate">{t("toolbar.font_family", "Sans Serif")}</span>
                <ChevronDown className="size-3 shrink-0" />
              </Button>
              <Button
                className="h-7 w-[70px] justify-between gap-0.5 px-2 text-xs"
                size="sm"
                type="button"
                variant="ghost"
              >
                <span className="truncate">{t("toolbar.font_size", "Normal")}</span>
                <ChevronDown className="size-3 shrink-0" />
              </Button>

              <div className="bg-border mx-0.5 h-5 w-px" />

              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Bold className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Italic className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Underline className="size-4" />
              </Button>
              <Button className="relative size-7" size="icon" type="button" variant="ghost">
                <span className="text-sm font-bold">A</span>
                <span className="absolute bottom-1.5 h-0.5 w-3 rounded-full bg-current" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Highlighter className="size-4" />
              </Button>

              <div className="bg-border mx-0.5 h-5 w-px" />

              <Button className="size-7" size="icon" type="button" variant="ghost">
                <List className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <ListOrdered className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <ListTodo className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <LinkIcon className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <ImageIcon className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Sparkles className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <Type className="size-4" />
              </Button>
              <Button className="size-7" size="icon" type="button" variant="ghost">
                <MoreHorizontal className="size-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-3 px-3 py-4 sm:px-4">
            {hasPendingSuggestion ? (
              <div className="bg-warning/15 border-warning rounded-xl border px-3 py-2 shadow-[0_2px_8px_hsl(var(--foreground)/0.06)]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-foreground flex items-center gap-2 text-xs font-medium sm:text-sm">
                    <Sparkles className="size-3 sm:size-4" />
                    <span>{t("pending_one", "1 AI suggestion pending")}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      className="h-6 px-2 text-xs sm:h-7"
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={handleAcceptPendingSuggestion}
                    >
                      {t("accept_all", "Accept all")}
                    </Button>
                    <Button
                      className="h-6 px-2 text-xs sm:h-7"
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={handleDiscardPendingSuggestion}
                    >
                      {t("discard_all", "Discard all")}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="text-foreground bg-card rounded-lg p-4 shadow-sm sm:p-6">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <h3>{t("doc.title", "Q4 Retention Summary")}</h3>
                <p>
                  {showAddedSection
                    ? t(
                        "doc.summary_applied",
                        "The brief now includes an economy section with pricing pressure, adoption barriers, and regional demand signals."
                      )
                    : t(
                        "doc.summary",
                        "Turn a strong answer into a shareable brief by tightening structure and tone."
                      )}
                </p>
                <h4>{t("doc.highlights_title", "Key drivers")}</h4>
                <ul>
                  {highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h4>{t("doc.next_steps_title", "Next steps")}</h4>
                <ol>
                  {nextSteps.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
                {showAddedSection ? (
                  <div
                    className={cn(
                      "relative rounded-md px-2 py-1.5 transition-colors",
                      hasPendingSuggestion && "ai-edit-highlight"
                    )}
                  >
                    <h4>{t("doc.added_section_title", "Economy section (added)")}</h4>
                    <p>
                      {t(
                        "doc.added_section_text",
                        "Economic uncertainty is extending sales cycles, so recommendations now include phased rollouts and cost-sensitive packaging."
                      )}
                    </p>
                    {hasPendingSuggestion ? (
                      <div className="bg-background/95 absolute top-1/2 -right-1 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded-md border p-0.5 shadow-sm backdrop-blur">
                        <Button
                          className="text-success hover:bg-success/10 hover:text-success size-6"
                          size="icon"
                          type="button"
                          variant="ghost"
                          onClick={handleAcceptPendingSuggestion}
                        >
                          <Check className="size-3.5" />
                        </Button>
                        <Button
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive size-6"
                          size="icon"
                          type="button"
                          variant="ghost"
                          onClick={handleDiscardPendingSuggestion}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="bg-background flex flex-col gap-2 rounded-lg border p-3">
              <p className="text-muted-foreground text-xs">
                {t(
                  "selection_hint",
                  "Highlight text and ask AI to explain, elaborate, or edit this exact section."
                )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  className="h-7 gap-1.5 px-2 text-xs"
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={handleGenerateProposal}
                >
                  <Sparkles className="size-3.5" />
                  {t("preview_ai_edit", "Generate AI edit preview")}
                </Button>
                <Button
                  className="h-7 gap-1.5 px-2 text-xs"
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={handleStartChatWithDocument}
                >
                  <MessageCirclePlus className="size-3.5" />
                  {t("chat_with_document", "Chat with document")}
                </Button>
              </div>
            </div>
            {showProposalBubble ? (
              <div className="bg-background/95 rounded-lg border p-3 shadow-lg backdrop-blur">
                <div className="mb-3 flex items-center gap-2">
                  <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
                    <Sparkles className="size-4" />
                  </div>
                  <p className="text-sm">{t("proposal_input", "What would you like to edit?")}</p>
                </div>
                <div className="flex flex-col gap-3 border-t pt-3 text-xs">
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                      {t("suggestion_explanation_title", "Explanation")}
                    </p>
                    <div className="bg-muted/35 max-h-40 overflow-y-auto rounded-md border px-3 py-2 text-sm">
                      {t(
                        "suggestion_explanation_text",
                        "This document is strong, but adding a compact economy section improves decision relevance and makes the recommendation set more complete."
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                      {t("suggestion_proposal_title", "Proposed edit")}
                    </p>
                    <div className="bg-muted/35 max-h-44 overflow-y-auto rounded-md border px-3 py-2 text-sm">
                      {t(
                        "suggestion_proposal_text",
                        "Add an economy section covering demand softness, cost pressure, and phased rollout guidance."
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                  <Button
                    className="h-7 px-2 text-xs"
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => setShowDiffPreview(true)}
                  >
                    {t("view_diff", "View diff")}
                  </Button>
                  <Button
                    className="h-7 px-2 text-xs"
                    size="sm"
                    type="button"
                    variant="ghost"
                    onClick={handleDiscardProposal}
                  >
                    {t("discard", "Discard")}
                  </Button>
                  <Button
                    className="h-7 px-2 text-xs"
                    size="sm"
                    type="button"
                    onClick={handleApplyProposal}
                  >
                    {t("apply", "Apply")}
                  </Button>
                </div>
              </div>
            ) : null}
            {showDiffPreview ? (
              <div className="bg-background overflow-hidden rounded-xl border shadow-xl">
                <div className="flex flex-col gap-3 border-b px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold sm:text-lg">
                      {t("preview_changes", "Preview Changes")}
                    </h3>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      {t("diff_operation", "AI edit")}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="bg-muted/30 flex items-center gap-1 rounded-lg border p-1">
                      <Button
                        size="sm"
                        type="button"
                        variant="ghost"
                        className={cn(
                          "h-7 px-2 text-xs",
                          diffViewMode === "side-by-side" && "bg-background shadow-sm"
                        )}
                        onClick={() => setDiffViewMode("side-by-side")}
                      >
                        <Columns2 className="size-3.5" />
                        {t("diff_side_by_side", "Side by Side")}
                      </Button>
                      <Button
                        size="sm"
                        type="button"
                        variant="ghost"
                        className={cn(
                          "h-7 px-2 text-xs",
                          diffViewMode === "inline-highlights" && "bg-background shadow-sm"
                        )}
                        onClick={() => setDiffViewMode("inline-highlights")}
                      >
                        <FileText className="size-3.5" />
                        {t("diff_inline", "Highlights")}
                      </Button>
                    </div>
                    <Button
                      className="h-7 px-2 text-xs"
                      size="sm"
                      type="button"
                      variant="outline"
                      onClick={() => setShowFullDiff((prev) => !prev)}
                    >
                      {showFullDiff ? (
                        <>
                          <Minimize2 className="size-3.5" />
                          {t("show_context", "Show Context")}
                        </>
                      ) : (
                        <>
                          <Maximize2 className="size-3.5" />
                          {t("show_full_diff", "Show Full Diff")}
                        </>
                      )}
                    </Button>
                    <div className="flex items-center gap-2 border-l pl-2">
                      <Button
                        className="h-7 px-2 text-xs"
                        size="sm"
                        type="button"
                        variant="ghost"
                        onClick={() => setShowDiffPreview(false)}
                      >
                        {t("close", "Close")}
                      </Button>
                      <Button
                        className="h-7 gap-1 px-2 text-xs"
                        size="sm"
                        type="button"
                        variant="outline"
                        onClick={handleRejectFromDiff}
                      >
                        <X className="size-3.5" />
                        {t("reject", "Reject")}
                      </Button>
                      <Button
                        className="h-7 gap-1 px-2 text-xs"
                        size="sm"
                        type="button"
                        onClick={handleAcceptFromDiff}
                      >
                        <Check className="size-3.5" />
                        {t("accept", "Accept")}
                      </Button>
                    </div>
                  </div>
                </div>
                {diffViewMode === "side-by-side" ? (
                  <div className="grid max-h-[18rem] grid-cols-2 divide-x overflow-hidden">
                    <div className="flex min-h-0 flex-col overflow-hidden">
                      <div className="bg-muted/30 shrink-0 border-b px-3 py-2 text-sm font-semibold">
                        {t("diff_original", "Original")}
                      </div>
                      <div className="overflow-y-auto p-2 text-xs sm:text-sm">
                        {displayOriginalLines.map((line, index) => (
                          <div
                            key={`left-${index}`}
                            className={cn(
                              "min-h-[1.5rem] px-2 py-0.5",
                              changedOriginalLines.has(index) &&
                                "bg-destructive/10 text-destructive border-destructive border-l-2"
                            )}
                          >
                            {line || " "}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex min-h-0 flex-col overflow-hidden">
                      <div className="bg-success/15 text-success shrink-0 border-b px-3 py-2 text-sm font-semibold">
                        {t("diff_modified", "Modified")}
                      </div>
                      <div className="overflow-y-auto p-2 text-xs sm:text-sm">
                        {displayModifiedLines.map((line, index) => (
                          <div
                            key={`right-${index}`}
                            className={cn(
                              "min-h-[1.5rem] px-2 py-0.5",
                              changedModifiedLines.has(index) &&
                                "border-success bg-success/15 border-l-2"
                            )}
                          >
                            {line || " "}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="max-h-[18rem] overflow-y-auto p-4">
                    <p className="text-muted-foreground mb-3 text-xs">
                      <span className="bg-success/15 text-foreground rounded px-1.5 py-0.5 font-medium">
                        {t("diff_added", "Added")}
                      </span>{" "}
                      •{" "}
                      <span className="bg-destructive/20 text-destructive inline-block rounded px-1.5 py-0.5 font-medium line-through">
                        {t("diff_removed", "Removed")}
                      </span>
                    </p>
                    <p className="font-mono text-xs leading-relaxed whitespace-pre-wrap sm:text-sm">
                      <span>
                        {t(
                          "diff_inline_before",
                          "### Key drivers\n- Onboarding drop-off around day 3.\n- Pricing objections tied to seat minimums.\n\n### Next steps\n- Add a guided 10-minute setup flow.\n- Launch a starter tier without seat minimums."
                        )}
                      </span>
                      <span className="bg-success/15 text-foreground rounded px-1 py-0.5 font-semibold">
                        {t(
                          "diff_inline_added",
                          "\n\n### Economy section\nEconomic uncertainty is extending sales cycles across EMEA and NA.\nRecommend phased rollout and cost-sensitive packaging options."
                        )}
                      </span>
                    </p>
                  </div>
                )}
                <div className="text-muted-foreground border-t px-4 py-2 text-xs">
                  {t("diff_stats", "Original")}: {originalDiffText.length}{" "}
                  {t("chars", "characters")} • {t("diff_modified", "Modified")}:{" "}
                  {modifiedDiffText.length} {t("chars", "characters")} • {t("change", "Change")}: +
                  {Math.max(0, modifiedDiffText.length - originalDiffText.length)}{" "}
                  {t("chars", "characters")}
                </div>
              </div>
            ) : null}
            {showChatHint && (
              <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
                <MessageCirclePlus className="text-primary size-4" />
                <span>
                  {t(
                    "chat_hint",
                    "Side-by-side chat opens with this document in context for larger updates."
                  )}
                </span>
              </div>
            )}
            {!showChatHint && hasPendingSuggestion && (
              <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
                <Sparkles className="text-primary size-4" />
                <span>
                  {t(
                    "pending_hint",
                    "The change is highlighted in the document. Accept or discard it inline or from the pending banner."
                  )}
                </span>
              </div>
            )}
            {!showChatHint && !hasPendingSuggestion && (
              <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
                <Sparkles className="text-primary size-4" />
                <span>
                  {showProposalBubble
                    ? t("ai_hint_preview", "AI proposal is ready for review.")
                    : t("ai_hint", "AI suggestions are ready for review.")}
                </span>
              </div>
            )}
            <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
              <span>
                {t(
                  "agent_hint",
                  "In real use, Chat with document can research and propose larger updates before you accept them."
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
